import { UUID } from "@bitory-gmbh/nidavellir";
import { NewSampleRequest } from "@/services/backend/samples/types/sample";
import { backendApi } from "@/services/backend/api";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { SurfaceMicrobialSampleSet } from "@/services/backend/samples/types/surface-microbial-sample";
import { Filter } from "@/shared/lib/filter/filter";
import { EntityId } from "@/shared/types/entity-id";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const samplesSurfaceMicrobialApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["SamplesSurfaceMicrobialSampleSet"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      /**
       * Creates a sample set with the defaults of the company's base data.
       * Fails with 404 if the company has no base data default for the
       * kind. Use the PUT endpoint to update a sample set.
       */
      samplesSurfaceMicrobialSampleSetCreate: build.mutation<
        SurfaceMicrobialSampleSet,
        CreateRequest
      >({
        query: (request) => ({
          url: `/samples/surface-microbial`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          "SamplesSurfaceMicrobialSampleSet",
          "SamplesSampleViewModel",
        ],
      }),
      samplesSurfaceMicrobialSampleSetPut: build.mutation<
        SurfaceMicrobialSampleSet,
        PutRequest
      >({
        query: (request) => ({
          url: `/samples/surface-microbial`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesSurfaceMicrobialSampleSet", id: request.id },
          "SamplesSurfaceMicrobialSampleSet",
          "SamplesSampleViewModel",
          // The backend derives the results of the report assessments of
          // the sample from its values, see SampleJudgementChanged.
          "HtzReport",
          "HtzSummary",
        ],
      }),
      samplesSurfaceMicrobialSampleSetUpdateLab: build.mutation<
        SurfaceMicrobialSampleSet,
        UpdateLabRequest
      >({
        query: (request) => ({
          url: `/samples/surface-microbial/lab`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesSurfaceMicrobialSampleSet", id: request.id },
          "SamplesSurfaceMicrobialSampleSet",
          "SamplesSampleViewModel",
        ],
      }),
      samplesSurfaceMicrobialSampleSetShow: build.query<
        SurfaceMicrobialSampleSet,
        ShowRequest
      >({
        query: (request) => ({
          url: `/samples/surface-microbial`,
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("SamplesSurfaceMicrobialSampleSet"),
      }),
      samplesSurfaceMicrobialSampleSetList: build.query<List, ListRequest>({
        query: (request) => ({
          url: `/samples/surface-microbial/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          providesList(result?.sampleSets, "SamplesSurfaceMicrobialSampleSet"),
      }),
      samplesSurfaceMicrobialSampleSetDelete: build.mutation<
        null,
        DeleteRequest
      >({
        query: (request) => ({
          url: `/samples/surface-microbial`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: [
          "SamplesSurfaceMicrobialSampleSet",
          "SamplesSampleViewModel",
        ],
      }),
    }),
  });

export const {
  useSamplesSurfaceMicrobialSampleSetCreateMutation,
  useSamplesSurfaceMicrobialSampleSetPutMutation,
  useSamplesSurfaceMicrobialSampleSetUpdateLabMutation,
  useSamplesSurfaceMicrobialSampleSetShowQuery,
  useSamplesSurfaceMicrobialSampleSetListQuery,
  useLazySamplesSurfaceMicrobialSampleSetListQuery,
  useSamplesSurfaceMicrobialSampleSetDeleteMutation,
} = samplesSurfaceMicrobialApi;

export type PutRequest = SurfaceMicrobialSampleSet;

export interface UpdateLabRequest {
  id: string;
  labId: EntityId;
}

export interface ShowRequest {
  id: string;
}

export type CreateRequest = NewSampleRequest;

export interface ListRequest {
  /**
   * Required by the backend: it must be active and hold at least one
   * company, otherwise the request fails its own validation.
   */
  company: Filter<UUID>;
  lab?: Filter<string>;
  document?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface List {
  sampleSets: SurfaceMicrobialSampleSet[];
  count: number;
}

export interface DeleteRequest {
  id: string;
}

/**
 * Patches the cached surface microbial sample set for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateSurfaceMicrobialSampleSetCache = updateCache(
  samplesSurfaceMicrobialApi,
  "samplesSurfaceMicrobialSampleSetShow",
);
